import api from './api';
import type {
  ColaboradorIdentificacaoPayloadContract,
  ColaboradorPortalPerfilContract,
  ColaboradorExameHistoricoContract,
  SolicitarCorrecaoDadosPayloadContract,
  SolicitarCorrecaoDadosResultadoContract,
  CriarDenunciaAnonimaPayloadContract,
  DenunciaAnonimaResultadoContract,
  ConsultaDenunciaContract,
  MinhaSaudeColaboradorContract,
} from '@sstexames/shared-types';

import { getCustomerApiBaseURL } from '../utils/apiConfig';

const isMobileClient = (): boolean => {
  if (typeof navigator === 'undefined') return false;
  return /android|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(navigator.userAgent);
};

export const portalColaboradorService = {
  /**
   * Identifica o colaborador por CPF e validação opcional de data de nascimento
   */
  async identificar(
    payload: ColaboradorIdentificacaoPayloadContract,
  ): Promise<ColaboradorPortalPerfilContract> {
    const response = await api.post<ColaboradorPortalPerfilContract>(
      '/portal-colaborador/identificar',
      payload,
    );
    return response.data;
  },

  /**
   * Lista todos os exames agendados, pendentes e realizados do colaborador
   */
  async getMeusExames(colaboradorId: number): Promise<ColaboradorExameHistoricoContract[]> {
    const response = await api.get<ColaboradorExameHistoricoContract[]>(
      '/portal-colaborador/meus-exames',
      {
        params: { colaborador_id: colaboradorId },
      },
    );
    return response.data;
  },

  /**
   * Solicita retificação de dados cadastrais ao RH
   */
  async solicitarCorrecaoDados(
    payload: SolicitarCorrecaoDadosPayloadContract,
  ): Promise<SolicitarCorrecaoDadosResultadoContract> {
    const response = await api.post<SolicitarCorrecaoDadosResultadoContract>(
      '/portal-colaborador/solicitar-correcao',
      payload,
    );
    return response.data;
  },

  /**
   * Retorna a URL direta da Guia de Encaminhamento
   */
  getGuiaUrl(agendamentoId: number, view = true): string {
    const base = getCustomerApiBaseURL();
    return `${base}/portal-colaborador/guia/${agendamentoId}${view ? '?view=true' : ''}`;
  },

  /**
   * Retorna a URL direta da Ficha Médica / Clínica
   */
  getFichaUrl(agendamentoId: number, view = true): string {
    const base = getCustomerApiBaseURL();
    return `${base}/portal-colaborador/ficha/${agendamentoId}${view ? '?view=true' : ''}`;
  },

  /**
   * Retorna a URL direta do ASO assinado
   */
  getAsoUrl(agendamentoId: number, view = true): string {
    const base = getCustomerApiBaseURL();
    return `${base}/portal-colaborador/aso/${agendamentoId}${view ? '?view=true' : ''}`;
  },

  /**
   * Realiza o download ou abertura direta no mobile do PDF da Guia de Encaminhamento
   */
  async downloadGuia(agendamentoId: number): Promise<void> {
    if (isMobileClient()) {
      const url = this.getGuiaUrl(agendamentoId, true);
      const opened = window.open(url, '_blank');
      if (!opened) {
        window.location.href = url;
      }
      return;
    }

    const response = await api.get(
      `/portal-colaborador/guia/${agendamentoId}`,
      { responseType: 'blob' },
    );
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `guia-agendamento-${agendamentoId}.pdf`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  },

  /**
   * Realiza o download ou abertura direta no mobile do PDF da Ficha Médica / Clínica
   */
  async downloadFicha(agendamentoId: number): Promise<void> {
    if (isMobileClient()) {
      const url = this.getFichaUrl(agendamentoId, true);
      const opened = window.open(url, '_blank');
      if (!opened) {
        window.location.href = url;
      }
      return;
    }

    const response = await api.get(
      `/portal-colaborador/ficha/${agendamentoId}`,
      { responseType: 'blob' },
    );
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `ficha-medica-agendamento-${agendamentoId}.pdf`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  },

  /**
   * Realiza o download ou abertura direta no mobile do PDF do ASO assinado
   */
  async downloadASO(agendamentoId: number): Promise<void> {
    if (isMobileClient()) {
      const url = this.getAsoUrl(agendamentoId, true);
      const opened = window.open(url, '_blank');
      if (!opened) {
        window.location.href = url;
      }
      return;
    }

    const response = await api.get(
      `/portal-colaborador/aso/${agendamentoId}`,
      { responseType: 'blob' },
    );
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `aso-agendamento-${agendamentoId}.pdf`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  },

  /**
   * Submete uma denúncia anônima com garantia de sigilo
   */
  async criarDenunciaAnonima(
    payload: CriarDenunciaAnonimaPayloadContract,
  ): Promise<DenunciaAnonimaResultadoContract> {
    const response = await api.post<DenunciaAnonimaResultadoContract>(
      '/denuncias-anonimas',
      payload,
    );
    return response.data;
  },

  /**
   * Consulta o andamento da averiguação da denúncia por protocolo
   */
  async consultarDenuncia(protocolo: string): Promise<ConsultaDenunciaContract> {
    const response = await api.get<ConsultaDenunciaContract>(
      `/denuncias-anonimas/consultar/${encodeURIComponent(protocolo)}`,
    );
    return response.data;
  },

  /**
   * Verifica se a empresa do colaborador possui o módulo de Saúde Corporativa contratado
   */
  async checkEmpresaHasSaude(empresaId: number): Promise<boolean> {
    try {
      const res = await api.get<{ has_saude: boolean }>(
        '/portal-colaborador/empresa-has-saude',
        { params: { empresa_id: empresaId } },
      );
      return Boolean(res.data?.has_saude);
    } catch {
      return false;
    }
  },

  /**
   * Obtém a visão completa "Minha Saúde" do colaborador
   */
  async getMinhaSaude(colaboradorId: number): Promise<MinhaSaudeColaboradorContract> {
    const response = await api.get<MinhaSaudeColaboradorContract>(
      `/portal-colaborador/${colaboradorId}/minha-saude`,
    );
    return response.data;
  },
};
